/**
 * grokLeadsStore — Simple pub/sub cache for the Grok AI Leads Queue.
 *
 * Shape intentionally mirrors `outboundStore.js` so the mental model is
 * familiar. The backend queue (`/api/grok/leads`) is fully REST so we
 * poll every 15s to keep the UI live without the complexity of a
 * WebSocket round-trip for a few dozen leads/hour.
 *
 * Public API:
 *   subscribe(fn)   → unsubscribe()
 *   getSnapshot()   → { leads, stats, loading, error }
 *   refresh()       → re-fetches everything immediately
 *   claim(leadId)   → atomic claim via POST /claim; resolves to the
 *                     fresh lead on success, throws Error('…') with
 *                     the backend message on 409.
 *   close(leadId)   → marks a claimed lead as closed/funded.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let state = {
  leads: [],
  stats: { new: 0, in_progress: 0, closed_today: 0 },
  loading: false,
  error: null,
};
const subs = new Set();
const emit = () => subs.forEach((fn) => fn(state));

function set(patch) {
  state = { ...state, ...patch };
  emit();
}

let pollHandle = null;

async function fetchAll() {
  try {
    const [leadsRes, statsRes] = await Promise.all([
      axios.get(`${API}/grok/leads`, { params: { limit: 2000 } }),
      axios.get(`${API}/grok/stats`),
    ]);
    set({
      leads: Array.isArray(leadsRes.data) ? leadsRes.data : [],
      stats: statsRes.data || state.stats,
      loading: false,
      error: null,
    });
  } catch (e) {
    // The backend returns 503 if GROK_WEBHOOK_TOKEN isn't configured —
    // we still want the UI to render, just with a friendly empty state.
    const detail = e?.response?.data?.detail || e?.message || 'Failed to load';
    set({ loading: false, error: detail });
  }
}

function start() {
  if (pollHandle) return;
  set({ loading: true });
  fetchAll();
  // 15s cadence — fast enough that a new lead feels "live" to the LO
  // scanning the queue, slow enough not to hammer the API.
  pollHandle = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    fetchAll();
  }, 30000);
}

function stop() {
  if (pollHandle) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
}

function subscribe(fn) {
  subs.add(fn);
  // Lazy start on first subscriber; stop when none remain.
  if (subs.size === 1) start();
  return () => {
    subs.delete(fn);
    if (subs.size === 0) stop();
  };
}

function getSnapshot() {
  return state;
}

async function refresh() {
  await fetchAll();
}

async function claim(leadId) {
  try {
    const r = await axios.post(`${API}/grok/leads/${leadId}/claim`, {});
    // Optimistic: patch the local cache so the UI updates instantly
    // without waiting for the next poll tick.
    const fresh = r.data;
    set({
      leads: state.leads.map((l) => (l.id === leadId ? { ...l, ...fresh } : l)),
    });
    // Also bump the stats locally (one less `new`, one more `in_progress`).
    set({
      stats: {
        ...state.stats,
        new: Math.max(0, state.stats.new - 1),
        in_progress: state.stats.in_progress + 1,
      },
    });
    return fresh;
  } catch (e) {
    // Refresh so the UI reflects the real owner that beat us to it.
    fetchAll();
    const msg = e?.response?.data?.detail || 'Could not claim this lead.';
    throw new Error(msg);
  }
}

async function close(leadId) {
  try {
    const r = await axios.post(`${API}/grok/leads/${leadId}/close`);
    const fresh = r.data;
    set({
      leads: state.leads.map((l) => (l.id === leadId ? { ...l, ...fresh } : l)),
      stats: {
        ...state.stats,
        in_progress: Math.max(0, state.stats.in_progress - 1),
        closed_today: state.stats.closed_today + 1,
      },
    });
    return fresh;
  } catch (e) {
    const msg = e?.response?.data?.detail || 'Could not close this lead.';
    throw new Error(msg);
  }
}

async function stampFirstCall(leadId) {
  try {
    const r = await axios.post(`${API}/grok/leads/${leadId}/first-call`);
    const fresh = r.data;
    set({ leads: state.leads.map((l) => (l.id === leadId ? { ...l, ...fresh } : l)) });
    return fresh;
  } catch { /* non-critical */ }
}

async function remove(leadId) {
  try {
    const r = await axios.delete(`${API}/grok/leads/${leadId}`);
    set({ leads: state.leads.filter((l) => l.id !== leadId) });
    fetchAll();
    return r.data;
  } catch (e) {
    throw new Error(e?.response?.data?.detail || 'Could not remove this lead.');
  }
}

export const grokLeadsStore = {
  remove,
  subscribe,
  getSnapshot,
  refresh,
  claim,
  close,
  stampFirstCall,
};
